import { h } from "./../../utils/createElement";
import { __assign, __extends } from "tslib";
import * as React from 'react';
import TabPane from './TabPane';
import { castInto, emptyFn } from '../../utils/helper';
import { ActiveTabIdContext } from './context';
import { consumeTheme } from '../../utils/theme';
import { horizontalScrollContainerBy, scrollIntoParentViewHorizontal } from '../../utils/containerScroll';
import debounce from 'lodash/debounce';
var TabPaneElementType = (h(TabPane, null)).type;
var indicatorWidthMap = {
    small: 24,
    medium: 28,
    large: 32,
};
var Tabs = (function (_super) {
    __extends(Tabs, _super);
    function Tabs() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.state = {
            currentActiveId: _this.props.defaultActiveId
        };
        _this.activeTabItemRef = React.createRef();
        _this.indicatorRef = React.createRef();
        _this.containerRef = React.createRef();
        _this.tabBarRef = React.createRef();
        _this.allItemIds = [];
        _this.selectTabBarItem = function (id) {
            if (id === _this.getActiveId()) {
                return;
            }
            _this.setState({ currentActiveId: id });
            _this.props.onChange(id);
        };
        _this.handleKeyDown = function (e) {
            if (!['ArrowLeft', 'ArrowRight'].includes(e.key)) {
                return;
            }
            e.preventDefault();
            var currentActiveIndex = _this.allItemIds.indexOf(_this.getActiveId());
            if (currentActiveIndex < 0) {
                return;
            }
            var nextActiveIndex = castInto(currentActiveIndex + (e.key === 'ArrowRight' ? 1 : -1), [0, _this.allItemIds.length - 1]);
            if (nextActiveIndex === currentActiveIndex) {
                return;
            }
            var nextId = _this.allItemIds[nextActiveIndex];
            _this.selectTabBarItem(nextId);
            setTimeout(function () {
                var _a;
                (_a = _this.activeTabItemRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }, 0);
        };
        _this.restoreTransitionLater = debounce(function () {
            setTimeout(function () {
                var indicatorDom = _this.indicatorRef.current;
                indicatorDom === null || indicatorDom === void 0 ? void 0 : indicatorDom.style.removeProperty('transition');
            }, 0);
        });
        _this.handleTabBarScroll = function () {
            _this.updateIndicator({ skipAnimation: true, scrollItemIntoView: false });
            _this.debouncedRerender();
        };
        _this.handleResize = function () {
            _this.updateIndicator({ skipAnimation: true });
        };
        _this.handleScrollLeft = function () {
            horizontalScrollContainerBy(_this.tabBarRef.current, -_this.props.scrollDistance);
        };
        _this.handleScrollRight = function () {
            horizontalScrollContainerBy(_this.tabBarRef.current, _this.props.scrollDistance);
        };
        _this.debouncedRerender = debounce(function () {
            _this.forceUpdate();
        }, 50);
        _this.themedRender = function (classNames) {
            var _a = _this.props, prefixCls = _a.prefixCls, className = _a.className, style = _a.style, children = _a.children, tabBarStyle = _a.tabBarStyle, indicatorStyle = _a.indicatorStyle, alignment = _a.alignment, indicatorClassName = _a.indicatorClassName, tabBarClassName = _a.tabBarClassName, tabBarAdditionalContent = _a.tabBarAdditionalContent, contentStyle = _a.contentStyle, contentClassName = _a.contentClassName, tabBarSize = _a.tabBarSize, tabBarContainerClassName = _a.tabBarContainerClassName, tabBarContainerStyle = _a.tabBarContainerStyle, onClickTabBarContainer = _a.onClickTabBarContainer;
            var activeId = _this.getActiveId();
            var cls = classNames(prefixCls, className);
            var tabBarContainerCls = classNames(prefixCls + "-bar-container", tabBarContainerClassName);
            var tabBarCls = classNames(prefixCls + "-bar", tabBarClassName, prefixCls + "-bar-" + alignment, prefixCls + "-bar-" + tabBarSize);
            var contentCls = classNames(prefixCls + "-content", contentClassName);
            var indicatorCls = classNames(prefixCls + "-indicator", indicatorClassName);
            return (h("div", { className: cls, style: style },
                h("div", { className: tabBarContainerCls, style: tabBarContainerStyle, onClick: onClickTabBarContainer },
                    h("ul", { className: tabBarCls, style: tabBarStyle, role: "tablist", onKeyDown: _this.handleKeyDown, ref: _this.tabBarRef, onScroll: _this.handleTabBarScroll }, _this.renderTabBarContent(classNames)),
                    h("div", { className: indicatorCls, style: indicatorStyle, ref: _this.indicatorRef }),
                    _this.renderScrollButtons(classNames),
                    tabBarAdditionalContent),
                h("div", { className: contentCls, style: contentStyle, ref: _this.containerRef },
                    h(ActiveTabIdContext.Provider, { value: activeId }, children))));
        };
        return _this;
    }
    Tabs.prototype.getActiveId = function () {
        var forceActiveId = this.props.activeId;
        var stateActiveId = this.state.currentActiveId;
        return forceActiveId || stateActiveId;
    };
    Tabs.prototype.updateIndicator = function (params) {
        if (params === void 0) { params = {}; }
        var _a = params.skipAnimation, skipAnimation = _a === void 0 ? false : _a, _b = params.scrollItemIntoView, scrollItemIntoView = _b === void 0 ? true : _b;
        var activeItemDom = this.activeTabItemRef.current;
        var indicatorDom = this.indicatorRef.current;
        var tabBarDom = this.tabBarRef.current;
        if (!activeItemDom || !indicatorDom || !tabBarDom) {
            return;
        }
        var targetLeft = activeItemDom.offsetLeft, targetWidth = activeItemDom.offsetWidth;
        var scrollLeft = scrollItemIntoView
            ? scrollIntoParentViewHorizontal(activeItemDom)
            : tabBarDom.scrollLeft;
        var indicatorFixedWidth = this.getIndicatorFixedWidth();
        var width = typeof indicatorFixedWidth === 'number' ? indicatorFixedWidth : targetWidth;
        indicatorDom.style.transform = "translateX(" + (targetLeft - scrollLeft + (targetWidth - width) / 2) + "px)";
        indicatorDom.style.width = width + "px";
        if (skipAnimation) {
            indicatorDom.style.transition = 'none';
            this.restoreTransitionLater();
        }
    };
    Tabs.prototype.getIndicatorFixedWidth = function () {
        var indicatorWidth = this.props.indicatorWidth;
        if (typeof indicatorWidth === 'number') {
            return indicatorWidth;
        }
        if (typeof indicatorWidth === 'string') {
            return indicatorWidthMap[indicatorWidth];
        }
        return null;
    };
    Tabs.prototype.componentDidUpdate = function () {
        this.updateIndicator({ scrollItemIntoView: false });
    };
    Tabs.prototype.componentDidMount = function () {
        this.updateIndicator({ skipAnimation: true });
        window.addEventListener('resize', this.handleResize);
        this.forceUpdate();
    };
    Tabs.prototype.componentWillUnmount = function () {
        window.removeEventListener('resize', this.handleResize);
    };
    Tabs.prototype.render = function () {
        return consumeTheme(this.themedRender);
    };
    Tabs.prototype.renderTabBarContent = function (classNames) {
        var _this = this;
        var _a = this.props, prefixCls = _a.prefixCls, children = _a.children, tabBarItemStyle = _a.tabBarItemStyle, activeItemStyle = _a.activeItemStyle, activeItemClassName = _a.activeItemClassName, tabBarItemClassName = _a.tabBarItemClassName, trigger = _a.trigger, tabBarSize = _a.tabBarSize;
        var activeId = this.getActiveId();
        this.allItemIds = [];
        return React.Children.map(children, function (child) {
            var _a, _b;
            if (child.type !== TabPaneElementType) {
                return null;
            }
            var _c = child.props, id = _c.id, tab = _c.tab, icon = _c.icon, activeIcon = _c.activeIcon;
            var isActive = id === activeId;
            var tabBarItemCls = classNames(prefixCls + "-bar-item", prefixCls + "-bar-item-" + tabBarSize, tabBarItemClassName, isActive && activeItemClassName, (_a = {},
                _a[prefixCls + "-bar-item-active"] = isActive,
                _a));
            var itemStyle = __assign(__assign({}, tabBarItemStyle), (isActive ? activeItemStyle : null));
            var effectiveIcon = isActive ? activeIcon || icon : icon;
            _this.allItemIds.push(id);
            return (h("li", __assign({ className: tabBarItemCls, style: itemStyle, key: id, ref: isActive ? _this.activeTabItemRef : function () { return null; }, "aria-selected": isActive, role: "tab", tabIndex: isActive ? 0 : -1 }, (_b = {}, _b[trigger === 'click' ? 'onClick' : 'onMouseEnter'] = function () { return _this.selectTabBarItem(id); }, _b)),
                effectiveIcon ?
                    typeof effectiveIcon === 'string' ?
                        h("span", { className: classNames(prefixCls + "-icon"), style: { backgroundImage: "url(\"" + effectiveIcon + "\")" } })
                        : effectiveIcon
                    : null,
                tab));
        });
    };
    Tabs.prototype.renderScrollButtons = function (classNames) {
        var _a, _b;
        var prefixCls = this.props.prefixCls;
        var tabBarDom = this.tabBarRef.current;
        if (!tabBarDom) {
            return null;
        }
        var scrollWidth = tabBarDom.scrollWidth, offsetWidth = tabBarDom.offsetWidth, scrollLeft = tabBarDom.scrollLeft;
        if (Math.abs(scrollWidth - offsetWidth) < 1) {
            return null;
        }
        var canScrollLeft = scrollLeft >= 1;
        var canScrollRight = offsetWidth + scrollLeft <= scrollWidth - 1;
        var leftButtonCls = classNames(prefixCls + "-scroll-btn", prefixCls + "-scroll-btn-left", (_a = {},
            _a[prefixCls + "-scroll-btn-visible"] = canScrollLeft,
            _a));
        var rightButtonCls = classNames(prefixCls + "-scroll-btn", prefixCls + "-scroll-btn-right", (_b = {},
            _b[prefixCls + "-scroll-btn-visible"] = canScrollRight,
            _b));
        return (h(React.Fragment, null,
            h("div", { className: leftButtonCls, onClick: this.handleScrollLeft }),
            h("div", { className: rightButtonCls, onClick: this.handleScrollRight })));
    };
    return Tabs;
}(React.Component));
export default Tabs;
Tabs.defaultProps = {
    prefixCls: 'dui-tabs',
    onChange: emptyFn,
    defaultActiveId: '',
    alignment: 'center',
    trigger: 'click',
    tabBarSize: 'medium',
    scrollDistance: 100,
};
